import { Box, Typography, useTheme } from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers"
import { OreveButton } from "components/button/OreveButton.tsx"
import dayjs, { Dayjs } from "dayjs"
import {
    OrevePopup,
    OrevePopupContent,
    OrevePopupTitle,
} from "components/popup/OrevePopup"
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useState,
} from "react"
import { Control, Controller, UseFormSetValue } from "react-hook-form"

interface DateRangePopupProps {
    open: boolean
    title?: string
    handleClose: () => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control: Control<any>
    setFromDate: Dispatch<SetStateAction<null | number>>
    setUntilDate: Dispatch<SetStateAction<null | number>>
    fromDate: null | number
    untilDate: null | number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setValue: UseFormSetValue<any>
}

export const DateRangePopup = ({ open,
    title = "Filter par date",
    control,
    handleClose,
    setFromDate,
    setUntilDate,
    fromDate,
    untilDate,
    setValue
}: DateRangePopupProps) => {
    const [isValidFrom, setIsValidFrom] = useState(true)
    const [isValidUntil, setIsValidUntil] = useState(true)
    const minDate = dayjs().subtract(3, 'month').startOf('month');

    const [fromDatePopup, setFromDatePopup] = useState<number>(-1)
    const [untilDatePopup, setUntilDatePopup] = useState<number>(
        Number.MAX_SAFE_INTEGER
    )

    const [invalidInterval, setInvalidInterval] = useState(false)
    const theme = useTheme()

    useEffect(() => {
        setInvalidInterval(
            isValidFrom && isValidUntil && fromDatePopup > untilDatePopup
        )
    }, [fromDatePopup, untilDatePopup, isValidFrom, isValidUntil])

    useEffect(() => {
        if (open) {
            setFromDatePopup(fromDate !== null ? fromDate : -1)
            setUntilDatePopup(
                untilDate !== null ? untilDate : Number.MAX_SAFE_INTEGER
            )
            setIsValidFrom(true)
            setIsValidUntil(true)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    const handleValidate = useCallback(() => {
        setFromDate(fromDatePopup !== -1 ? fromDatePopup : null)
        setUntilDate(
            untilDatePopup !== Number.MAX_SAFE_INTEGER ? untilDatePopup : null
        )
        setValue("date_from", fromDatePopup)
        setValue("date_to", untilDatePopup)
        handleClose()
    }, [
        fromDatePopup,
        untilDatePopup,
        setFromDate,
        setUntilDate,
        handleClose,
        setValue,
    ])

    return (
        <OrevePopup open={open} handleClose={handleClose}>
            <OrevePopupTitle>
                <Typography variant="headlineSmall" component="div">
                    {title}
                </Typography>
            </OrevePopupTitle>
            <OrevePopupContent>
                <Box sx={{
                    display: "flex",
                    gap: "1rem",
                    mb: 2,
                    '& .MuiFormControl-root': {
                        flex: 1
                    }
                }}>
                    <Controller
                        name="from"
                        control={control}
                        render={() => (
                            <DatePicker
                                label="Début"
                                value={fromDatePopup != -1 ? dayjs(fromDatePopup) : null}
                                minDate={minDate}
                                onChange={(date: Dayjs | null = null) => {
                                    let value = -1
                                    if (date) {
                                        const fromTimestamp = date.valueOf()
                                        if (
                                            !Number.isNaN(fromTimestamp) &&
                                            fromTimestamp >= 0 &&
                                            date.isAfter(minDate.subtract(1, 'day')) // S'assurer que la date est après minDate
                                        ) {
                                            value = fromTimestamp
                                        }
                                    }
                                    setFromDatePopup(value)
                                }}
                                onError={(error) => setIsValidFrom(error === null)}
                                slotProps={{
                                    field: {
                                        clearable: true,
                                    },
                                    textField: {
                                        fullWidth: true,
                                        variant: "filled",
                                        error: !isValidFrom || invalidInterval,
                                        InputProps: {
                                            disableUnderline: true,
                                        },
                                        sx: {
                                            backgroundColor: theme.palette.grey[100],
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="until"
                        control={control}
                        render={() => (
                            <DatePicker
                                label="Fin"
                                value={untilDatePopup !== Number.MAX_SAFE_INTEGER ? dayjs(untilDatePopup) : null}
                                onChange={(date: Dayjs | null = null) => {
                                    let value = Number.MAX_SAFE_INTEGER
                                    if (date) {
                                        const fromTimestamp = date.valueOf()
                                        if (
                                            !Number.isNaN(fromTimestamp) &&
                                            fromTimestamp >= 0
                                        ) {
                                            value = fromTimestamp
                                        }
                                    }
                                    setUntilDatePopup(value)
                                }}
                                onError={(error) => setIsValidUntil(error === null)}
                                slotProps={{
                                    field: {
                                        clearable: true,
                                    },
                                    textField: {
                                        fullWidth: true,
                                        variant: "filled",
                                        error: !isValidUntil || invalidInterval,
                                        InputProps: {
                                            disableUnderline: true,
                                        },
                                        sx: {
                                            backgroundColor: theme.palette.grey[100],
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>

                {invalidInterval && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        La date de début doit être antérieure à la date de fin
                    </Typography>
                )}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "1.5rem",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: "1.5rem"
                    }}
                >
                    <OreveButton
                        value="Annuler"
                        variant="outlined"
                        color="primary"
                        onClick={handleClose}
                    />
                    <OreveButton
                        value="Valider"
                        variant="contained"
                        color="primary"
                        onClick={handleValidate}
                        disabled={!(isValidFrom && isValidUntil) || invalidInterval}
                    />
                </Box>
            </OrevePopupContent>
        </OrevePopup>
    )
}